/**
 * Module 1: Why Speaking Feels Scary
 *
 * In which Philemon pours the first cup badly, and nobody talks him out of
 * his fear.
 * Primary character: Philemon | Supporting: Xenia, Theron
 *
 * REWORKED 2026-10-04 as the worked sample for the "less laboured" pass
 * (docs/CONTENT_REVIEW_2026-10-04.md). The rules it follows, so the other
 * seven evenings can be brought into line with it:
 *
 *   1. One idea, shown once. Tonight's is: the fear does not have to leave
 *      before you speak. Philemon's hands shake, the cup gets poured anyway.
 *      No guide states the moral, and nobody says "Exactly."
 *   2. Something happens tonight. A small thing: the first cup.
 *   3. Somebody is wrong and stays wrong. Theron thinks a man can simply
 *      decide not to be afraid, and he still thinks so when the evening ends.
 *   4. The reader gets a turn inside the scene (the `yourTurn` block).
 *   5. A box carries only what the guides cannot know, in under ninety
 *      words, and never follows another box. The rest of the research waits,
 *      closed, in the `notes` block at the end.
 *   6. One exercise in the scene, one to take away, and the tool for the rest.
 *
 * CONTINUITY this evening must keep (later evenings depend on it):
 *   - Philemon froze at nineteen before forty scholars, for twenty heartbeats,
 *     then hid for three years (Evening 2: "after those three years of hiding").
 *   - His father's face on every audience (Evening 5: the eulogy for his father).
 *   - Theron was twenty-six in Carthage (Evening 7 tells that story in full).
 *     Xenia only names it here; do not tell it.
 *
 * Every study below was checked against its abstract or record on 2026-10-04;
 * see docs/audits/authenticity-audit-finding-your-voice-2026-10.md.
 */

export const MODULE_01_CONTENT = [
  // --- The first cup ---
  {
    type: 'narration',
    text: 'The gate is unlatched when you arrive, left that way for you, you suspect. The lamps are lit and the low table is laid, but nobody is eating. Xenia holds the wine jug. Theron has his cup out already. Philemon is studying the empty cup in front of him with great care.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'Our guest is here. In this house the first cup is poured with a few words for the evening, and tonight the words are Philemon\'s.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'She gives it to whoever wants it least. I have never once been asked.',
  },
  {
    type: 'narration',
    text: 'Philemon stands. He takes the jug, and its lip rattles against the cup. Wine goes over his fingers and onto the stone. He says the words anyway, to the gods and to the house and to whoever has come in from the street, and they come out thin and too fast. Then he sits down and wipes his hand on his knee.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'Thirty years of teaching rhetoric at the Lyceum. You would think a cup of wine would have stopped frightening me.',
  },
  {
    type: 'narration',
    text: 'You had not noticed his hands until the jug rattled. You might not have noticed then, if he had not looked down at them himself.',
  },
  {
    type: 'research',
    title: 'They see less than you think',
    source: 'Savitsky and Gilovich, Journal of Experimental Social Psychology, 2003',
    paragraphs: [
      'In 2003 Kenneth Savitsky and Thomas Gilovich had students give short unprepared speeches in pairs. Afterwards each speaker rated how nervous they had looked, and how nervous their partner had looked.',
      'Speakers thought their nerves showed more than their partners saw. From the inside a shaking hand is enormous. From across the room it is a hand. When speakers were told this before they began, the effect eased.',
    ],
  },

  // --- The worst day ---
  {
    type: 'heading',
    text: 'The worst day',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Thirty years. What is left to be afraid of? You know more about speaking than any man in Athens.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'Knowing has nothing to do with it. I knew everything on the worst day of my life too. I was nineteen. My first formal argument before the senior scholars. I had prepared for weeks. I could have recited the opening in my sleep.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'I stood up. Forty pairs of eyes turned towards me. And everything went. My argument, my opening line, my own name. I stood there for what felt like an hour. It was perhaps twenty heartbeats. Then I sat down.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Without saying anything?',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'Without a word. And for three years after that I made sure nobody could ask me to. I turned things down. I let other men present my ideas. I called it strategy.',
  },
  {
    type: 'research',
    title: 'Why the words went',
    source: 'Arnsten, Nature Reviews Neuroscience, 2009',
    paragraphs: [
      'The part of the brain you use to hold a thread and find the next word is the prefrontal cortex. It is also the part most easily knocked off course by stress.',
      'Amy Arnsten\'s 2009 review of the research found that even quite mild stress, of the kind you cannot control, can cause a rapid loss of those abilities. A blank mind in front of forty people is that system under load. It says nothing about what you know.',
    ],
  },

  // --- Theron's cure ---
  {
    type: 'heading',
    text: 'Theron\'s cure',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Then here is your cure, and I will not charge for it. Decide not to be afraid. I did. I have talked my way onto ships and off them, in harbours where nobody wanted to hear me. A man chooses.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'Does he. You tell the Carthage story where the big man laughs and buys double. There is an earlier one.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'There is not.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'You were twenty-six.',
  },
  {
    type: 'narration',
    text: 'Theron finds something to look at in his cup. It is the first time tonight he has had nothing to say, and Xenia lets it stay that way for a while. Then she turns to you, as though the two of you had been talking all along.',
  },
  {
    type: 'yourTurn',
    id: 'm1-what-goes-first',
    character: 'xenia',
    speaker: 'Xenia',
    prompt: 'And you? When you picture yourself standing up to speak, what goes first?',
    options: [
      {
        label: 'My mind. It empties.',
        reply: {
          character: 'philemon',
          speaker: 'Philemon',
          text: 'Then we are the same animal. Mine went at nineteen and took my own name with it. Stay tonight. That is the one I know most about.',
        },
      },
      {
        label: 'My body. Hands, voice, face.',
        reply: {
          character: 'philemon',
          speaker: 'Philemon',
          text: 'Then you watched yourself a moment ago, when I poured. Tell me honestly: did you see my hands before I looked at them? Most people do not.',
        },
      },
      {
        label: 'I start rehearsing how it will go wrong.',
        reply: {
          character: 'xenia',
          speaker: 'Xenia',
          text: 'You hold the disaster in full before anyone has heard a word. I know that habit. It feels like preparing, and it leaves you more tired than the speech would have.',
        },
      },
      {
        label: 'I find a reason not to speak at all.',
        reply: {
          character: 'theron',
          speaker: 'Theron',
          text: 'A merchant\'s answer. No cargo, no shipwreck. No trade either. Philemon tried it for three years. Ask him what it bought.',
        },
      },
    ],
  },

  // --- Whose face ---
  {
    type: 'heading',
    text: 'Whose face',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'There is one audience we have not named, and it is never in the room. When you imagine them judging you, whose face is it? The people who came? Or someone older. A teacher who was sharp with you. A cousin who laughed. Yourself, younger, deciding you were not worth hearing.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'For me it was my father\'s. For years, every audience I stood before wore his face.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'The harshest critic in most rooms was never invited. We carry them in, and then we speak to them instead of to the people who came.',
  },
  {
    type: 'exercise',
    number: 1,
    title: 'Whose face?',
    instructions: 'Answer Xenia\'s question for yourself. It only works with a name and a face, so be specific.',
    prompts: [
      'When you imagine being judged as you speak, whose voice is it in? Whose face does the audience wear?',
      'When did that voice first turn up in your life?',
      'Will that person be in the room the next time you speak?',
      'Now picture the people who will be there. What do they most likely want from you?',
    ],
  },

  // --- What changed ---
  {
    type: 'heading',
    text: 'What changed',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'So what changed? You teach. You stand in front of students every day. Either you are cured or you are lying to them.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'Neither. I stopped waiting for it to go. That was all of it. You saw me pour. My hands did what they have done since I was nineteen, and the cup still got poured.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Badly.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'Badly. It is on the stone and on my knee. And the evening has begun, which it would not have if I had waited until I was steady.',
  },
  {
    type: 'narration',
    text: 'He turns to you then, properly, his full attention on your face.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'You came through that gate carrying something. Everyone who finds this courtyard does. A meeting you dread. Words you have been swallowing. A moment you walked away from. I will not tell you the fear is nothing. I will tell you that I do not shake over things I do not care about, and I doubt you do either.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'I still say a man could simply decide.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'You have said so twice. Say it a third time and I will ask about Carthage again.',
  },
  {
    type: 'narration',
    text: 'Theron drinks instead. Xenia fills your cup without asking, and the evening goes on from there.',
  },

  {
    type: 'divider',
  },

  // --- To take away ---
  {
    type: 'heading',
    text: 'Before the next evening',
  },
  {
    type: 'exercise',
    number: 2,
    title: 'Your ladder',
    instructions: 'List the speaking situations that make you anxious, from mildly uncomfortable to the ones you would cross the street to avoid. Then put them in order.',
    prompts: [
      'Include one-to-one conversations, small groups and larger rooms, at work, at home and with strangers.',
      'Beside each, note what sets the fear off: being watched, being judged, forgetting your words, looking foolish.',
      'Number them from 1 (least frightening) to 10 (most).',
      'Mark the ones you rated 3 to 5. Those are where to practise first: uncomfortable enough to count, small enough to survive.',
    ],
  },
  {
    type: 'toolLink',
    toolId: 'fear-companion',
    title: 'Fear Companion',
    text: 'Philemon\'s tool takes one fear at a time: where it sits in your body, whose voice it speaks in and what it is trying to protect. It keeps what you write.',
  },

  // --- The research, for whoever wants it ---
  {
    type: 'notes',
    title: 'What we know now',
    intro: 'Philemon, Xenia and Theron can only tell you what they have seen. Here is what has been tested since.',
    items: [
      {
        title: 'Being left out registers as distress',
        paragraphs: [
          'In 2003 Naomi Eisenberger, Matthew Lieberman and Kipling Williams scanned people\'s brains while they played a ball-tossing game on a screen and were then left out of it. A region called the anterior cingulate cortex, which is also active when the body is in pain, was busier during the exclusion, and busiest in the people who said they felt worst.',
          'A 2014 study by Choong-Wan Woo and colleagues found that rejection and physical pain leave different patterns in that region, so the two are not the same thing. The plainer finding stands: being shut out is felt as distress, and dreading it is not silly.',
        ],
        source: 'Eisenberger, Lieberman and Williams, Science, 2003; Woo and colleagues, Nature Communications, 2014',
      },
      {
        title: 'Telling yourself not to be nervous',
        paragraphs: [
          'In 1987 Daniel Wegner and colleagues asked people to say their thoughts aloud for five minutes while trying not to think of a white bear, and to ring a bell each time they did. They could not keep it out. Asked afterwards to think about the bear on purpose, they thought of it more than people who had been allowed to from the start.',
          '"Don\'t be nervous" is an instruction of the same kind, which is one reason Theron\'s cure does not work on Philemon.',
        ],
        source: 'Wegner, Schneider, Carter and White, Journal of Personality and Social Psychology, 1987',
      },
      {
        title: 'What to tell yourself instead',
        paragraphs: [
          'Most people believe the thing to do with nerves before a performance is to calm down. In 2014 Alison Wood Brooks tested another approach with karaoke singing, public speaking and maths.',
          'People who told themselves the feeling was excitement, in words as simple as saying "I am excited" out loud, felt more excited and performed better than people who tried to calm down. The racing heart is the same either way. What changes is what you take it to mean.',
        ],
        source: 'Brooks, Journal of Experimental Psychology: General, 2014',
      },
      {
        title: 'This fear does shift',
        paragraphs: [
          'A 2019 pooling of 30 trials, with 1,355 people between them, found that psychological help for the fear of public speaking works. Most of the trials tested cognitive or behavioural approaches. The effect was clear when the help ended and larger at follow-up: people kept improving afterwards. The authors also saw some signs that trials with weaker results were less likely to be published.',
          'None of those approaches was staying away. Avoiding a room feels like relief, which is why Philemon could do it for three years, and it left the fear exactly where it was.',
        ],
        source: 'Ebrahimi, Pallesen, Kenter and Nordgreen, Frontiers in Psychology, 2019',
      },
    ],
  },

  // --- Module Summary ---
  {
    type: 'summary',
    characterId: 'philemon',
    points: [
      'A blank mind or a shaking hand under pressure is a body doing what bodies do. It says nothing about what you know.',
      'Your nerves show less than they feel.',
      'The fear does not have to go before you speak. Philemon\'s never went.',
    ],
    nextPreview: {
      dialogue: [
        {
          character: 'xenia',
          text: 'Tomorrow I have a question for Philemon, and he will not thank me for it.',
        },
        {
          character: 'philemon',
          text: 'She has been saving it. I can tell by the way she is not looking at me.',
        },
      ],
    },
  },
];

export const MODULE_01_EXERCISES = [
  {
    number: 1,
    title: 'Whose face?',
    description: 'Name whose voice your imagined audience speaks in.',
    xpReward: 20,
  },
  {
    number: 2,
    title: 'Your ladder',
    description: 'List and order the speaking situations that make you anxious.',
    xpReward: 20,
  },
];

export const MODULE_01_SUMMARY = {
  title: 'Why Speaking Feels Scary',
  keyInsight: 'The fear does not have to go before you speak.',
  characterQuote: {
    character: 'philemon',
    text: 'The trembling never fully leaves. But it no longer speaks for me.',
  },
  takeaways: [
    'A blank mind under pressure says nothing about what you know.',
    'Your nerves show less than they feel.',
    'The fear does not have to go before you speak.',
  ],
};
